import { FC, useEffect, useState } from "react";

import { Box, Collapse } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import EmailInput from "@components/EmailInput";
import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import PhoneInput from "@components/PhoneInput";
import TextInput from "@components/TextInput";
import { useRouter } from "@lib/router";
import { AddressBookSearchInput } from "@modules/address-book-search";
import { useAuth } from "@modules/auth";
import { LockerInput } from "@modules/locker-search";
import { DOMAIN } from "@utils/constants";

import { OrderFields, ORDER_MESSAGE_ZONE, ORDER_FIELDS_MAX_LENGTHS } from "../../constants";
import useRecipientForm from "../../hooks/common/useRecipientForm";
import useScrollToRecipient from "../../hooks/common/useScrollToRecipient";
import useOrderFormik from "../../hooks/useOrderFormik";
import { NewRecipient, UpdateRecipient } from "../../submodules/OrderRecipient";
import ArrivalMessage from "./ArrivalMessage";
import SectionTitle from "./SectionTitle";
import ReceiverPhoneNotificationMessage from "./ReceiverPhoneNotificationMessage";
import { findCountryCode } from "@components/PhoneInput/utils/findCountryCode";

const ReceiverLockerFormFields: FC = () => {
  const { t } = useTranslation();

  const { isLoggedIn } = useAuth();

  const { values } = useOrderFormik();

  const setMessage = useMessage();
  const destinationCountry = values[OrderFields.DESTINATION_COUNTRY];
  const isIntraEurope = values[OrderFields.IS_INTRA_EUROPE];
  const hasNewAddress = values[OrderFields.HAS_NEW_ADDRESS];

  const receiverAddressId = values[OrderFields.RECEIVER_ADDRESS_ID];

  const { defaultLocale } = useRouter();
  const [ phoneCountryCode, setPhoneCountryCode ] = useState<string | null | undefined>(null);
  const isReceiverEmailRequired = (defaultLocale === "lt" || defaultLocale === "lv") && isIntraEurope;

  const {
    isNewRecipientFormExpanded,
    expandNewRecipientForm,
    handleAddressBookItemSelected,
    handleNewRecipientExpandChanged,
  } = useRecipientForm();

  const handleLockerSearchError = (error?: string) => {
    setMessage({
      message: error || t("order-message-failed_locker_search"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
  };

  useEffect(() => {
    const phoneNumber = values[OrderFields.RECEIVER_PHONE_NUMBER];
    const phoneNumberCode = phoneNumber ? findCountryCode(phoneNumber) : null;
    if (phoneNumberCode) {
      setPhoneCountryCode(phoneNumberCode);
    }
  }, [values[OrderFields.RECEIVER_PHONE_NUMBER]])

  useEffect(() => {
    if (hasNewAddress) {
      expandNewRecipientForm();
    }
  }, [expandNewRecipientForm, hasNewAddress]);

  const recipientRef = useScrollToRecipient();

  const isNewRecipientFormVisible = isLoggedIn && (!receiverAddressId || hasNewAddress);
  const isUpdateRecipientFormVisible = isLoggedIn && !!receiverAddressId && !hasNewAddress;

  return (
    <FullWidthVStack spacing="1rem" ref={recipientRef}>
      <FullWidthVStack spacing="1rem">
        <SectionTitle>{t("order-receiver-receiver_details")}</SectionTitle>
        {isLoggedIn && (
          <AddressBookSearchInput
            id="order-receiver-locker-address_book_search"
            type="locker"
            countryCode={destinationCountry!}
            onAddressSelect={handleAddressBookItemSelected}
          />
        )}
      </FullWidthVStack>
      <Collapse in={!isLoggedIn || !isNewRecipientFormExpanded} style={{overflow: "initial !important"}}>
        <FullWidthVStack spacing="1rem">
          <Box data-field={OrderFields.RECEIVER_NAME}>
            <TextInput
              name={OrderFields.RECEIVER_NAME}
              label={`${t("order-receiver-name")} ${t("common-optional")}`}
              id="order-receiver-name"
              maxLength={ORDER_FIELDS_MAX_LENGTHS.name}
            />
          </Box>
          <Box data-field={OrderFields.RECEIVER_PHONE_NUMBER}>
            <PhoneInput
              name={OrderFields.RECEIVER_PHONE_NUMBER}
              label={t("order-receiver-mobile_number")}
              id="order-receiver-phone"
              pinnedPhoneCodes={DOMAIN.DOMAIN_PHONE_NUMBERS[defaultLocale || ""]}
              onPhoneCodeChange={setPhoneCountryCode}
              defaultCountry={values[OrderFields.DESTINATION_COUNTRY]!}
            />
          </Box>
          <ReceiverPhoneNotificationMessage phoneCountryCode={phoneCountryCode} />
          <Box data-field={OrderFields.RECEIVER_EMAIL}>
            <EmailInput
              name={OrderFields.RECEIVER_EMAIL}
              label={`${t("order-receiver-email")} ${!isReceiverEmailRequired ? t("common-optional") : ""}`}
              id="order-receiver-email"
              maxLength={ORDER_FIELDS_MAX_LENGTHS.email}
            />
          </Box>
          <LockerInput
            countryCode={destinationCountry!}
            lockerCodeFieldName={OrderFields.RECEIVER_LOCKER}
            lockerNameFieldName={OrderFields.RECEIVER_LOCKER_NAME}
            lockerPostalCodeFieldName={OrderFields.RECEIVER_ZIP_CODE}
            lockerStreetName={OrderFields.RECEIVER_STREET}
            lockerCityName={OrderFields.RECEIVER_CITY}
            label={t("order-receiver-choose_locker")}
            isDisabled={!destinationCountry}
            onError={handleLockerSearchError}
            id="order-receiver-locker"
          />
        </FullWidthVStack>
      </Collapse>
      {isNewRecipientFormVisible && (
        <NewRecipient
          isExpanded={isNewRecipientFormExpanded}
          country={destinationCountry!}
          onExpandChange={handleNewRecipientExpandChanged}
          isLockerForm
        />
      )}
      {isUpdateRecipientFormVisible && <UpdateRecipient />}
      <ArrivalMessage />
    </FullWidthVStack>
  );
};
export default ReceiverLockerFormFields;
